import { checkPropNullOrUndefined, getPopupContainer } from '@bimser/core';
import { DatePicker, GetRef } from 'antd';
import * as React from 'react';
import Styles from './assets/styles.scss';
import {
	DatePickerMode,
	IDateRangePickerProps,
	RangePickerPresetRange,
	RangePickerValue
} from './entities';

import classNames from 'classnames/bind';
import { BCPortalStyleExtractor } from '../BCPortalStyleExtractor';
const cx = classNames.bind(Styles);

type BCDateRangePickerRef = GetRef<typeof DatePicker.RangePicker>;

const BCDateRangePicker = React.memo(
	React.forwardRef(
		(props: IDateRangePickerProps, ref: React.ForwardedRef<GetRef<typeof DatePicker>>) => {
			const innerRef = React.useRef<GetRef<any>>(null);
			React.useImperativeHandle(ref, () => innerRef.current);

			const onChange = (dates: RangePickerValue, dateStrings: [string, string]) => {
				if (props.onChange) {
					let startDate = null;
					let endDate = null;

					if (dates) {
						startDate = dates[0] ? dates[0].format() : null;
						endDate = dates[1] ? dates[1].format() : null;
					}

					props.onChange(dates, [startDate, endDate], dateStrings);
				}

				if (
					(props.value?.[0] && !dateStrings[0]) ||
					(props.value?.[1] && !dateStrings[1])
				) {
					innerRef.current?.blur();
				}
			};

			const getFormat = () => {
				if (props.format) {
					return props.format;
				}
				return props.showTime ? 'L HH:mm:ss' : 'L';
			};

			const getPopupContainerFn = (node: HTMLDivElement) => {
				const candidateGeneralTab =
					getPopupContainer(node)?.parentNode?.parentNode?.parentNode;
				return candidateGeneralTab?.id?.startsWith('rc-tabs-')
					? candidateGeneralTab
					: document.body;
			};

			const className = cx({
				dateRangePickerStyles: true,
				textalignRight: props.textAlign === 'right',
				textalignLeft: props.textAlign === 'left',
				textalignCenter: props.textAlign === 'center',
				[props.className]: true
			});

			const renderPicker = () => (
				<DatePicker.RangePicker
					ref={innerRef}
					picker={props.mode}
					getPopupContainer={props.getPopupContainer || getPopupContainerFn}
					autoFocus={props.autoFocus}
					className={className}
					defaultValue={props.defaultValue}
					onChange={onChange}
					placeholder={props.placeholder}
					size={props.size}
					disabled={props.disabled}
					disabledDate={props.disabledDate}
					showTime={props.showTime}
					showToday={props.showToday}
					format={props.mode === 'date' || !props.mode ? getFormat() : undefined}
					style={props.style}
					value={props.value}
					onBlur={props.onBlur}
					onFocus={props.onFocus}
					onKeyDown={props.onKeyDown}
					allowClear={props.allowClear && !props.readOnly}
					disabledTime={props.disabledTime}
					open={props.readOnly ? false : undefined}
					inputReadOnly={props.readOnly}
					{...checkPropNullOrUndefined(props, 'disabledTime')}
					{...checkPropNullOrUndefined(props, 'onPanelChange')}
					{...checkPropNullOrUndefined(props, 'renderExtraFooter')}
					{...checkPropNullOrUndefined(props, 'onCalendarChange')}
					{...checkPropNullOrUndefined(props, 'ranges')}
					{...checkPropNullOrUndefined(props, 'disabledDate')}
					{...checkPropNullOrUndefined(props, 'allowEmpty')}
				/>
			);

			if (props.extractPopupKey) {
				return (
					<BCPortalStyleExtractor extractPopupKey={props.extractPopupKey}>
						{renderPicker()}
					</BCPortalStyleExtractor>
				);
			}

			return renderPicker();
		}
	)
);

export default BCDateRangePicker;
export {
	BCDateRangePickerRef,
	DatePickerMode,
	IDateRangePickerProps,
	RangePickerPresetRange,
	RangePickerValue
};
